import { useState } from "react";
import { useFormContext } from "react-hook-form";

import type { LineAdditionalServicesNotesFormValues } from "../interfaces";

import { getBrasilApiCepUrl, getViaCepUrl } from "@/core/libs/api/constants";

interface LineAdditionalServiceNotesFormValues {
  notes: LineAdditionalServicesNotesFormValues;
}

interface ViaCepResponse {
  cep?: string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: "true";
}

interface BrasilApiCepResponse {
  cep?: string;
  state?: string;
  city?: string;
  neighborhood?: string;
  street?: string;
  name?: string;
  message?: string;
}

interface Address {
  city: string;
  state: string;
  street: string;
  neighborhood: string;
}

export interface MissingAddressFields {
  street: boolean;
  neighborhood: boolean;
}

function formatZipCodeInput(value: string): string {
  const zipCode = value.replace(/\D/g, "").slice(0, 8);

  return zipCode.length > 5
    ? `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`
    : zipCode;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Falha ao consultar o CEP: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchAddress(zipCode: string): Promise<Address> {
  try {
    const data = await fetchJson<ViaCepResponse>(getViaCepUrl(zipCode));

    if (data.erro) {
      throw new Error("CEP não encontrado no ViaCEP.");
    }

    return {
      city: data.localidade ?? "",
      state: data.uf ?? "",
      street: data.logradouro ?? "",
      neighborhood: data.bairro ?? ""
    };
  } catch {
    const data = await fetchJson<BrasilApiCepResponse>(
      getBrasilApiCepUrl(zipCode)
    );

    if (data.name === "CepPromiseError") {
      throw new Error(data.message ?? "CEP não encontrado na BrasilAPI.");
    }

    return {
      city: data.city ?? "",
      state: data.state ?? "",
      street: data.street ?? "",
      neighborhood: data.neighborhood ?? ""
    };
  }
}

export function useZipCodeLookup() {
  const { clearErrors, setError, setValue } =
    useFormContext<LineAdditionalServiceNotesFormValues>();

  const [isLookingUpZipCode, setIsLookingUpZipCode] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);
  const [missingAddressFields, setMissingAddressFields] =
    useState<MissingAddressFields>({
      street: false,
      neighborhood: false
    });

  const lookupZipCode = async (zipCode: string) => {
    const normalizedZipCode = zipCode.replace(/\D/g, "");

    setValue(
      "notes.deliveryAddress.zipCode",
      formatZipCodeInput(normalizedZipCode)
    );
    setHasAddress(false);
    setMissingAddressFields({ street: false, neighborhood: false });

    if (normalizedZipCode.length !== 8) {
      if (normalizedZipCode.length > 0) {
        setError("notes.deliveryAddress.zipCode", {
          message: "CEP deve conter 8 dígitos."
        });
      } else {
        clearErrors("notes.deliveryAddress.zipCode");
      }

      return;
    }

    setIsLookingUpZipCode(true);

    try {
      const address = await fetchAddress(normalizedZipCode);

      setValue("notes.deliveryAddress.city", address.city);
      setValue("notes.deliveryAddress.state", address.state);
      setValue("notes.deliveryAddress.street", address.street);
      setValue("notes.deliveryAddress.neighborhood", address.neighborhood);
      setMissingAddressFields({
        street: !address.street,
        neighborhood: !address.neighborhood
      });
      clearErrors("notes.deliveryAddress.zipCode");
      setHasAddress(true);
    } catch {
      setError("notes.deliveryAddress.zipCode", {
        message: "CEP não encontrado ou inválido."
      });
    } finally {
      setIsLookingUpZipCode(false);
    }
  };

  return {
    hasAddress,
    isLookingUpZipCode,
    lookupZipCode,
    missingAddressFields
  };
}
